import React, { createContext, useState, useEffect, ReactNode, useRef, useMemo } from "react";
import {
    ArrowRightIcon,
    BinocularsIcon,
    BoxIcon,
    ChartNetworkIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClipboardListIcon,
    DraftingCompassIcon,
    FileTextIcon,
    GraduationCapIcon,
    HandshakeIcon,
    IterationCcwIcon,
    MegaphoneIcon,
    SearchIcon,
    SquareChartGanttIcon,
    WrenchIcon,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { ProjectDraft, WorkstreamDeliverableIcon } from "tekkr-common/dist/model/project/project";
import { generateId } from "tekkr-common/dist/model/project/project-iteration";
import AnimateHeight from "react-animate-height";
import { v4 } from "uuid";
import { AssistantLoadingIndicator, MessageView } from "../../components/shared/chat/message";
import { ActionPlaceholderContext, TekkrMarkdown } from "../../components/shared/markdown/tekkr-markdown";
import { Button } from "../../components/ui/button";
import { useCurrentOrgUser } from "../../auth/org-provider";
import { ChatInputBox, ChatInputBoxOnSend, FileUpload } from "../../components/shared/chat/chat-input-box";
import { DraftPlanPreview, isDraftReadyToShowPreview, ReviseDraftPlanPreview } from "./components/draft-preview";
import { cn } from "../../lib/utils";
import { useInvalidateChatListQueries } from "../../data/queries/list-chats";
import { mapUploadsToAttachments, useChatCredentials, useSdkChatWrapper } from "../../data/hooks/use-chat";
import { ErrorBox } from "../../components/shared/chat/error-box";
import { useChatError } from "../../data/hooks/use-chat-error";
import { Message } from "@ai-sdk/react";
import { useCreateCreateProjectChatMutation } from "../../data/mutations/create-create-project-chat";
import { GoogleDriveChatBanner } from "../../components/shared/google-drive/google-drive-chat-banner";

interface InitialMessage {
    content: string;
    uploads: FileUpload[];
    sent: boolean;
}

const deliverableIconMap: Record<WorkstreamDeliverableIcon, typeof BoxIcon> = {
    [WorkstreamDeliverableIcon.hire]: HandshakeIcon,
    [WorkstreamDeliverableIcon.analysis]: ChartNetworkIcon,
    [WorkstreamDeliverableIcon.iteration]: IterationCcwIcon,
    [WorkstreamDeliverableIcon.implementation]: WrenchIcon,
    [WorkstreamDeliverableIcon.report]: ClipboardListIcon,
    [WorkstreamDeliverableIcon.document]: FileTextIcon,
    [WorkstreamDeliverableIcon.strategy]: BinocularsIcon,
    [WorkstreamDeliverableIcon.roadmap]: SquareChartGanttIcon,
    [WorkstreamDeliverableIcon.training]: GraduationCapIcon,
    [WorkstreamDeliverableIcon.communication]: MegaphoneIcon,
    [WorkstreamDeliverableIcon.audit]: SearchIcon,
    [WorkstreamDeliverableIcon.charter]: FileTextIcon,
    [WorkstreamDeliverableIcon.design]: DraftingCompassIcon,
};

export interface CreateProjectChatPageApi {
    get chatId(): string;
    get reviseProjectId(): string | null;
}

export function iconForDeliverable(deliverable: { icon: string }): typeof ArrowRightIcon | undefined {
    const iconKey = WorkstreamDeliverableIcon[
        deliverable.icon as unknown as WorkstreamDeliverableIcon
    ] as unknown as WorkstreamDeliverableIcon;
    return deliverableIconMap[iconKey];
}

function TranscriptCollapsible(props: { transcript: string }) {
    const [collapsed, setCollapsed] = useState(true);
    return (
        <div className={"flex flex-col gap-1"}>
            <Button
                size={"sm"}
                className={"self-start"}
                onClick={() => setCollapsed(!collapsed)}
                variant={"outline-primary"}
            >
                {collapsed && (
                    <>
                        Show transcript <ChevronDownIcon className={"ms-2 h-5 w-5"} />
                    </>
                )}
                {!collapsed && (
                    <>
                        Hide transcript <ChevronUpIcon className={"ms-2 h-5 w-5"} />
                    </>
                )}
            </Button>
            <AnimateHeight height={collapsed ? 0 : "auto"}>
                <div className={"rounded-md bg-accent p-4 opacity-85"}>
                    <TekkrMarkdown markdown={props.transcript} />
                </div>
            </AnimateHeight>
        </div>
    );
}

function matchPlan(message: string): { match: string; plan: ProjectDraft } | null {
    const planMatch = message.match(/\+\+\+ PLAN START \+\+\+(.|\n)*\+\+\+ PLAN END \+\+\+/gm);
    if (!planMatch) {
        return null;
    }

    const plan = JSON.parse(
        planMatch[0].replace(/\+\+\+ PLAN START \+\+\+/, "").replace(/\+\+\+ PLAN END \+\+\+/, "")
    ) as ProjectDraft;
    plan.workstreams?.forEach((ws) => {
        ws.id = generateId();
        ws.deliverables.forEach((d) => {
            d.id = generateId();
        });
    });
    return { match: planMatch[0], plan };
}

function markdownifyUserMessage(message: string, role: Message["role"]): string {
    if (role !== "user") {
        return message;
    }
    return message.replaceAll("\n", "\n\n");
}

function MessageComp(props: { message: Message; page: CreateProjectChatPageApi; isLastMessage: boolean }) {
    const inMessageComponents: Record<string, ReactNode> = {};
    let message = props.message.content.replaceAll(
        /\+\+\+\s?TRANSCRIPT START\s?\+\+\+(.|\n|\s)*?\+\+\+\s?TRANSCRIPT END\s?\+\+\+/gm,
        (match) => {
            const id = v4();
            const transcript = match.replaceAll(/\+\+\+\s?TRANSCRIPT (START|END)\s?\+\+\+/g, "").trim();
            inMessageComponents[id] = <TranscriptCollapsible transcript={transcript} />;
            return `:tekkr_action_placeholder[]{#${id}}`;
        }
    );
    const planMatch = matchPlan(message);
    if (planMatch) {
        message = props.message.content.replaceAll(planMatch.match, "");
    }
    return (
        <MessageView message={props.message}>
            <ActionPlaceholderContext.Provider value={(id) => inMessageComponents[id]}>
                <TekkrMarkdown markdown={markdownifyUserMessage(message, props.message.role)} />
            </ActionPlaceholderContext.Provider>
        </MessageView>
    );
}

export const CreateProjectChatIdContext = createContext<string | null>(null);

const welcomeMessageNewProject =
    "Please tell me about a challenge you're facing or a change you'd like to make to your organization.";
const welcomeMessageRevise = "Please let me know how and why you would like to change the plan for this project.";

export function CreateProjectChatPage(props: { reviseProjectId?: string }) {
    const [query, setQuery] = useSearchParams();
    const chatId = query.get("chat_id") ?? null;
    const invalidateChatListQuery = useInvalidateChatListQueries();
    const setChatId = (id: string) => {
        query.set("chat_id", id);
        setQuery(query, { replace: true });
        void invalidateChatListQuery();
    };

    const pageApi: CreateProjectChatPageApi = {
        get chatId(): string {
            return chatId!;
        },
        get reviseProjectId(): string | null {
            return props.reviseProjectId ?? null;
        },
    };

    const credentials = useChatCredentials();

    const createProjectChatMutation = useCreateCreateProjectChatMutation();
    const [initialMessage, setInitialMessage] = useState<InitialMessage | undefined>(undefined);
    const startChat: ChatInputBoxOnSend = async (msg, uploads) => {
        setInitialMessage({ content: msg, uploads, sent: false });
        const chatId = await createProjectChatMutation.mutateAsync({
            reviseProjectId: props.reviseProjectId,
        });
        setChatId(chatId);
    };

    return (
        <CreateProjectChatIdContext.Provider value={chatId}>
            {!chatId && (
                <StartChatContent
                    initialMessage={initialMessage}
                    isPending={createProjectChatMutation.isPending}
                    pageApi={pageApi}
                    onStartChat={startChat}
                />
            )}
            {credentials && chatId && (
                <ChatContent
                    initialMessage={initialMessage}
                    chatId={chatId}
                    credentials={credentials}
                    pageApi={pageApi}
                />
            )}
        </CreateProjectChatIdContext.Provider>
    );
}

function ChatContent(props: {
    chatId: string;
    credentials: Record<string, string>;
    initialMessage?: InitialMessage;
    pageApi: CreateProjectChatPageApi;
    reviseProjectId?: string;
}) {
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    const {
        messages,
        status,
        sendMessage,
        error: sdkError,
        reload,
    } = useSdkChatWrapper(props.chatId, props.credentials);

    const { displayError, isError, handleRetry } = useChatError(sdkError, reload);
    const isPending = status !== "ready" && !isError;

    const currentPlan = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m.role === "assistant") {
                const match = matchPlan(m.content);
                if (match && isDraftReadyToShowPreview(match.plan)) {
                    return match.plan;
                }
            }
        }
        return null;
    }, [messages, messages.length]);

    const splitView = !!currentPlan || !!props.reviseProjectId;

    const handleSend: ChatInputBoxOnSend = (msg, uploads) => {
        void sendMessage(msg, mapUploadsToAttachments(uploads));
    };

    useEffect(() => {
        if (props.initialMessage && !props.initialMessage.sent) {
            handleSend(props.initialMessage.content, props.initialMessage.uploads);
            props.initialMessage.sent = true;
        }
    }, [props.initialMessage]);

    return (
        <div className={"flex flex-row items-start justify-center gap-6 py-[5vh] lg:gap-10 xl:gap-14"}>
            <div className={cn("w-full", splitView ? "create-project-chat-column-width" : "max-w-[60vw]")}>
                <div
                    ref={messagesContainerRef}
                    className={cn(
                        "fixed top-[48pt] max-h-[calc(100vh-48pt-48pt)] w-full overflow-y-scroll px-4 pb-4 pt-[5vh]",
                        splitView ? "create-project-chat-column-width" : "max-w-[60vw]"
                    )}
                >
                    <div className={"flex flex-col gap-6 pb-6"}>
                        <InitialAssistantMessage
                            isLastMessage={false}
                            pageApi={props.pageApi}
                            reviseProjectId={props.reviseProjectId}
                        />
                        {messages.map((m, i) => (
                            <MessageComp
                                message={m}
                                key={i}
                                page={props.pageApi}
                                isLastMessage={i === messages.length - 1}
                            />
                        ))}
                        {isPending && <AssistantLoadingIndicator />}
                        {displayError && <ErrorBox error={displayError} onRetry={handleRetry} className="mb-4" />}
                    </div>
                    <ChatInputBox onSend={handleSend} />
                </div>
            </div>
            <div className={cn("transition-all", !splitView ? "max-w-0" : "create-project-chat-column-width")}>
                {currentPlan && (
                    <DraftPlanPreview
                        page={props.pageApi}
                        message={""}
                        draft={currentPlan}
                        interactionsEnabled={true}
                    />
                )}
                {!currentPlan && props.reviseProjectId && (
                    <ReviseDraftPlanPreview
                        page={props.pageApi}
                        message={""}
                        projectId={props.reviseProjectId}
                        interactionsEnabled={true}
                    />
                )}
            </div>
        </div>
    );
}

function StartChatContent(props: {
    onStartChat: ChatInputBoxOnSend;
    reviseProjectId?: string;
    pageApi: CreateProjectChatPageApi;
    initialMessage?: InitialMessage;
    isPending: boolean;
}) {
    return (
        <div className={"flex flex-row items-start justify-center gap-6 py-[5vh] lg:gap-10 xl:gap-14"}>
            <div className={cn("max-w-[60svw]")}>
                <div className={"flex flex-col gap-6 pb-6"}>
                    <InitialAssistantMessage
                        isLastMessage={!props.initialMessage}
                        pageApi={props.pageApi}
                        reviseProjectId={props.reviseProjectId}
                    />
                    <GoogleDriveChatBanner />
                    {props.initialMessage && (
                        <MessageComp
                            message={{
                                id: "initial",
                                role: "user",
                                content: props.initialMessage.content,
                            }}
                            page={props.pageApi}
                            isLastMessage={true}
                        />
                    )}
                    {props.isPending && <AssistantLoadingIndicator />}
                </div>
                <ChatInputBox disabled={props.isPending} onSend={props.onStartChat} />
            </div>
        </div>
    );
}

function InitialAssistantMessage(props: {
    isLastMessage: boolean;
    reviseProjectId?: string;
    pageApi: CreateProjectChatPageApi;
}) {
    const user = useCurrentOrgUser();
    return (
        <MessageComp
            message={{
                id: "placeholder",
                role: "assistant",
                content: `**Hey, ${user.name.split(" ")[0]}.** 👋  \n${props.reviseProjectId ? welcomeMessageRevise : welcomeMessageNewProject}`,
            }}
            page={props.pageApi}
            isLastMessage={props.isLastMessage}
        />
    );
}
